import { BASE_WS_URL, env } from '../constants/env';
import { store } from '../store';
import { replay } from '../services/websocketService';
import { updateLatestWebsocketTimestamp, forceFullSync } from '../actions/actions';
import { SET_WS_CONNECTED } from '../actions/actionTypes';
import { __IOS__, __WEB__ } from '../constants/styles';

let ws,
  pingTimeout,
  reconnectTimeoutms = 100,
  initialized = false,
  handleMessage = null;

function heartbeat() {
  clearTimeout(pingTimeout);
  pingTimeout = setTimeout(() => {
    // console.info('>>>>>> WEBSOCKET PING');
    _closeIt();
  }, 30000 + 2000);
}

function onOpen() {
  // console.info(`>>>>>>>>>>> Websocket handleOpen... `);
  if (!ws) return;

  reconnectTimeoutms = 100;
  heartbeat();
  let isAuthed = true;
  const authenticate = {
    bearer: store.getState().user.token || store.getState().user.oktaAuthToken,
  };
  if (!authenticate.bearer) isAuthed = false;

  authenticate['op-platform'] = __WEB__ ? 'web' : 'native';
  if (store.getState().user.oktaAuthToken) {
    authenticate.nonce = store.getState().user.oktaNonce;
    authenticate['op-user-id'] = store.getState().user.userId;
    if (!authenticate['op-user-id'] || !authenticate.nonce) {
      isAuthed = false;
    }
  }
  if (!isAuthed) {
    closeWS();
    return;
  }
  try {
    //authenticate
    ws.send(JSON.stringify(authenticate));
    const ts = store.getState().application.latestWebSocketTimestamp;
    // console.info('>>>>>> WEBSOCKET OPEN', !!ws, ts);
    if (ts) {
      replayLatestMessages(ts);
    } else {
      // should not be here with no ts, but just in case send them through
      // sync and then the ts will be set
      store.dispatch(forceFullSync({ doLogout: false }));
      if (!store.getState().application.wsConnected) {
        store.dispatch({ type: SET_WS_CONNECTED, payload: true });
      }
    }
  } catch (e) {
    console.error('Error opening websocket connection: ', e);
  }
}

function onClose(event) {
  // console.info('>>>>>> WEBSOCKET handleClose', event);
  const wsInstance = event.target;
  wsInstance?.removeEventListener?.('open', onOpen);
  wsInstance?.removeEventListener?.('close', onClose);
  wsInstance?.removeEventListener?.('message', onMessage);
  wsInstance?.removeEventListener?.('error', onError);

  // if we got in here without having called _closeIt then call it to cleanup
  // but only if we dont have a new ws already initialized
  if (ws && ws === wsInstance) {
    _closeIt();
  }
}

function onMessage(event) {
  // console.info(`~~~~ Websocket event: `, event);
  // console.info(`~~~~ Websocket event?.data `, event?.data);
  if (!ws) return;

  heartbeat();
  if (event?.data === ``) {
    // console.info(`~~~~ Websocket reply heartbeat `, new Date())
    try {
      ws.send('');
    } catch (e) {
      // console.info('>>>> websocket heartbeat send error', e);
      // this probably just means the ws errored or is closed
    }
  } else {
    const eventDataObj = JSON.parse(event.data);
    // console.log('MESSAGE - updateLatestWebsocketTimestamp', eventDataObj, eventDataObj.created_at);
    store.dispatch(updateLatestWebsocketTimestamp(eventDataObj.created_at));
    handleMessage(eventDataObj).catch((e) =>
      console.error('Error in websockets handleMessage: ', e)
    );
  }
}

function onError(err) {
  // if its a token expired error then we should log out
  // if we've gone into the background then we will call openWS again when we come back
  // console.info('>>>>>> WEBSOCKET handleError', err);
  if (
    err.response?.httpStatusCode === 401 ||
    err.message?.startsWith('Software caused connection abort')
  ) {
    closeWS();
  } else {
    console.error('Websocket error', err);
    _closeIt();
  }
}

function _openIt() {
  // console.info('>>> _openIt')
  if (!initialized || !!ws) return;

  ws = new WebSocket(env(BASE_WS_URL), null, {
    headers: {
      'User-Agent': 'react-native',
    },
  });
  ws.addEventListener('open', onOpen);
  ws.addEventListener('close', onClose);
  ws.addEventListener('message', onMessage);
  ws.addEventListener('error', onError);
}
function _closeIt() {
  if (store.getState().application.wsConnected) {
    store.dispatch({ type: SET_WS_CONNECTED, payload: false });
  }
  if (!ws) return;
  // console.info('>>> _closeIt')
  ws.removeEventListener?.('open', onOpen);
  ws.removeEventListener?.('close', onClose);
  ws.removeEventListener?.('message', onMessage);
  ws.removeEventListener?.('error', onError);
  ws.close();
  ws = null;
  clearTimeout(pingTimeout);
  if (initialized) {
    setTimeout(_openIt, Math.min(10000, (reconnectTimeoutms += reconnectTimeoutms)));
  }
}

// The public API - the initialized flag tells us if we're supposed to be open or not
function initWS(messageHandler) {
  // console.info('>>>> initWS');
  // return;
  if (initialized) {
    return;
  }
  initialized = true;
  if (messageHandler) handleMessage = messageHandler;
  _openIt();
}
function closeWS() {
  // console.info('>>>>>> closeWS');
  initialized = false;
  _closeIt();
}

async function replayLatestMessages(after) {
  try {
    // after = 1000;
    // console.info('>>>> replayLatestMessages', after);
    const response = await replay(after);
    if (response === 'SYNC') {
      store.dispatch(forceFullSync({ doLogout: false }));
    }
    if (!!ws && !!initialized && !store.getState().application.wsConnected) {
      store.dispatch({ type: SET_WS_CONNECTED, payload: true });
    }
  } catch (err) {
    // console.error('Error on Websocket replay', err);
    // console.info('>>> replayLatestMessages error, status code', err.response?.httpStatusCode);
    if (err.response?.httpStatusCode === 401) {
      // console.info('>>> Unauthorized on replay, closing WS');
      closeWS();
    } else {
      // if there is some unknown error just send through full sync
      // dont close the WS so that we dont go through the whole initWS process and get into
      // a loop. If the user gets logged out via fullsync then the WS will be closed anyway
      store.dispatch(forceFullSync({ doLogout: false }));
      if (!!ws && !!initialized && !store.getState().application.wsConnected) {
        store.dispatch({ type: SET_WS_CONNECTED, payload: true });
      }
    }
  }
}

export { initWS, closeWS };
